import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * The page's one left edge.
 *
 * There were three. `core-lifecycle` was `max-w-3xl` (336px in from the window
 * at 1440), `clients-section` and `more-section` were `max-w-5xl` (208px), and
 * the header and footer put their `px-6` *inside* the wrapper (232px) — so two
 * adjacent left-aligned headings sat 128px apart. Nobody chose that: the class
 * string was copied four times and edited twice.
 *
 * Every section, the header and the footer wrap their content in this, at one
 * nesting depth, and the horizontal padding lives here rather than on the
 * `<section>` — which is what keeps the edge identical whether or not a section
 * paints a full-bleed background behind it.
 *
 * **A narrow measure is not a narrow container.** The core lifecycle still wants
 * its text set to ~42rem; it caps the *text*, inside this, rather than pulling
 * the whole section in and taking its heading with it.
 *
 * **One width, hero included.** 80rem, up from `max-w-5xl`: at 1024px the
 * header's wordmark and "Log in" sat 770px in from each edge of a 2560px
 * screen, and the hero had to break out of the column to hold its headline,
 * which put the page on two different widths again. Nothing passes its own
 * `max-w-*` here — change the width in this one place or not at all.
 */
export function Container({
  as: Tag = 'div',
  className,
  style,
  children,
}: {
  as?: 'div' | 'section'
  className?: string
  style?: CSSProperties
  children: ReactNode
}) {
  return (
    <Tag
      className={cn('mx-auto w-full max-w-7xl px-6', className)}
      style={style}
    >
      {children}
    </Tag>
  )
}
